import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';

// Settings → Shipping → "Carrier pickups & priority" (guided P&P phase 3, 2026-10-17; docs/GUIDED-PP.md).
// Fernando: Packeta 10:00, DPD/Posti/Omniva 14:00, Bring 16:00 if not earlier; a passed pickup goes last.
// Backend: backend/pickup_schedule.py (app_settings 'carrier_pickups').

interface Group { key: string; label: string; carrier: string; country?: string }
interface ClosedDate { date: string; carriers: string[]; note: string }
interface Settings { groups: Group[]; times: Record<string, Record<string, string | null>>; closed_dates: ClosedDate[]; ee_holidays_closed: boolean; packeta_remind_min?: number }
interface Ranked extends Group { deadline: string | null; today: boolean; minutes_left: number | null }

const input = 'bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1 text-sm';
const btn = 'px-2 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-40';
const DAY_LABEL: Record<string, string> = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

export default function PickupScheduleCard() {
  const [s, setS] = useState<Settings | null>(null);
  const [carriers, setCarriers] = useState<Record<string, string>>({});
  const [weekdays, setWeekdays] = useState<string[]>([]);
  const [ranked, setRanked] = useState<Ranked[]>([]);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState('');

  const loadRanked = () => authFetch(`${API}/pickups/today`).then(r => r.ok ? r.json() : null)
    .then(d => d && setRanked(d.groups || [])).catch(() => {});
  useEffect(() => {
    authFetch(`${API}/settings/carrier-pickups`).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return; setS(d.settings); setCarriers(d.carriers || {}); setWeekdays(d.weekdays || []);
    }).catch(() => {});
    loadRanked();
  }, []);
  if (!s) return null;

  const change = (patch: Partial<Settings>) => { setS({ ...s, ...patch }); setDirty(true); };
  const move = (i: number, d: number) => {
    const g = [...s.groups]; const j = i + d;
    if (j < 0 || j >= g.length) return;
    [g[i], g[j]] = [g[j], g[i]]; change({ groups: g });
  };
  const setTime = (c: string, day: string, v: string) =>
    change({ times: { ...s.times, [c]: { ...(s.times[c] || {}), [day]: v || null } } });
  const save = async () => {
    const r = await authFetch(`${API}/settings/carrier-pickups`, { method: 'PUT', body: JSON.stringify(s) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || 'Could not save'); return; }
    setS(d.settings); setDirty(false); setMsg('✓ Saved'); loadRanked(); setTimeout(() => setMsg(''), 2500);
  };
  const fmt = (iso: string | null) => {
    if (!iso) return 'no pickup known';
    const d = new Date(iso);
    return d.toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4">
      <div className="text-white text-sm font-medium">Carrier pickups & priority</div>
      <div className="text-gray-500 text-xs mt-0.5 mb-3">
        A P&P round takes the orders of the carrier whose <b className="text-gray-300">next pickup</b> comes first; a pickup that has
        already been today counts as tomorrow's, so it goes after today's later ones. Equal times go by the order of the list.
        Bring follows the HRX ship schedule once that is read automatically; until then the Bring times below apply.
      </div>

      <div className="text-gray-400 text-xs uppercase tracking-widest mb-1">Order (ties)</div>
      <div className="space-y-1 mb-4">
        {s.groups.map((g, i) => (
          <div key={g.key} className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 w-5 text-right">{i + 1}.</span>
            <input value={g.label} onChange={e => change({ groups: s.groups.map((x, k) => k === i ? { ...x, label: e.target.value } : x) })}
              className={`${input} w-28`} />
            <span className="text-xs text-gray-500 w-40">{carriers[g.carrier] || g.carrier}{g.country ? ` → ${g.country}` : ''}</span>
            <button className={btn} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button className={btn} disabled={i === s.groups.length - 1} onClick={() => move(i, 1)}>↓</button>
          </div>
        ))}
      </div>

      <div className="text-gray-400 text-xs uppercase tracking-widest mb-1">Pickup times (empty = no pickup that day)</div>
      <div className="overflow-x-auto mb-4">
        <table className="text-sm">
          <thead><tr><th></th>{weekdays.map(d => <th key={d} className="text-xs text-gray-500 font-normal px-1">{DAY_LABEL[d]}</th>)}</tr></thead>
          <tbody>
            {Object.keys(carriers).map(c => (
              <tr key={c}>
                <td className="text-gray-300 pr-2">{carriers[c]}</td>
                {weekdays.map(d => (
                  <td key={d} className="px-0.5 py-0.5">
                    <input value={(s.times[c] || {})[d] || ''} onChange={e => setTime(c, d, e.target.value.replace(/[^\d:]/g, '').slice(0, 5))}
                      placeholder="—" className={`${input} keep-width w-16 text-center`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Fernando 3.11: the Packeta handover button glows before the pickup; at the pickup the station calls the person */}
      <label className="flex flex-wrap items-center gap-2 text-sm text-gray-300 mb-4">
        The Packeta handover button glows
        <input type="number" min={0} max={120} value={s.packeta_remind_min ?? 10}
          onChange={e => change({ packeta_remind_min: Math.max(0, Math.min(120, Number(e.target.value) || 0)) })} className={`${input} w-20`} />
        minutes before the Packeta pickup; at the pickup time the P&amp;P station asks the person to start the handover
      </label>

      <div className="text-gray-400 text-xs uppercase tracking-widest mb-1">Days without pickups</div>
      <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
        <input type="checkbox" checked={s.ee_holidays_closed} onChange={e => change({ ee_holidays_closed: e.target.checked })} />
        No pickups on Estonian public holidays
      </label>
      <div className="space-y-1 mb-3">
        {s.closed_dates.map((cd, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
            <input type="date" value={cd.date} onChange={e => change({ closed_dates: s.closed_dates.map((x, k) => k === i ? { ...x, date: e.target.value } : x) })} className={input} />
            <select value={cd.carriers[0] || '*'} onChange={e => change({ closed_dates: s.closed_dates.map((x, k) => k === i ? { ...x, carriers: [e.target.value] } : x) })} className={input}>
              <option value="*">all carriers</option>
              {Object.keys(carriers).map(c => <option key={c} value={c}>{carriers[c]}</option>)}
            </select>
            <input value={cd.note} onChange={e => change({ closed_dates: s.closed_dates.map((x, k) => k === i ? { ...x, note: e.target.value } : x) })} placeholder="note" className={`${input} w-40`} />
            <button className={btn} onClick={() => change({ closed_dates: s.closed_dates.filter((_, k) => k !== i) })}>Remove</button>
          </div>
        ))}
        <button className={btn} onClick={() => change({ closed_dates: [...s.closed_dates, { date: new Date().toISOString().slice(0, 10), carriers: ['*'], note: '' }] })}>+ Add a day</button>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={!dirty} className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-40">Save</button>
        {msg && <span className="text-sm text-green-400">{msg}</span>}
      </div>

      {ranked.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-800">
          <div className="text-gray-400 text-xs uppercase tracking-widest mb-1">A round started now would go in this order</div>
          <ol className="text-sm text-gray-300 list-decimal list-inside">
            {ranked.map(g => (
              <li key={g.key}>{g.label} <span className="text-gray-500">— {fmt(g.deadline)}{g.today && g.minutes_left != null ? ` (in ${Math.floor(g.minutes_left / 60)} h ${g.minutes_left % 60} min)` : ''}</span></li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
